/**
 * Notifications SSE client.
 *
 * Uses @microsoft/fetch-event-source instead of the native EventSource
 * for one reason: we need to send `Authorization: Bearer …` with the
 * in-memory access token. Native EventSource doesn't allow custom
 * request headers and our backend doesn't issue an access-token cookie,
 * so cookie-based auth isn't an option for this endpoint.
 *
 * The lifecycle this module owns:
 *
 *   - Open the stream with the current access token.
 *   - On 401: refresh the access token via the api-client's shared
 *     refresh (authApi.refreshAccessFromCookie), then reopen with the
 *     new token. Going through the shared path is important: it is
 *     single-flighted (one in-flight /api/auth/refresh at a time) and
 *     handles native token persistence. Rolling our own refresh here
 *     used to race the api-client's on token expiry, and because refresh
 *     ROTATES the token, two concurrent refreshes tripped the backend's
 *     reuse detector and revoked the whole device's session — a spurious
 *     same-day logout. The lib retries internally using the same
 *     `headers` object, which would re-send the stale token — so on 401
 *     we abort and start a clean fetch instead.
 *   - On any other error (network blip, server restart): exponential
 *     backoff capped at 30s.
 *   - On disconnect mid-stream: the backend writes `id: <n.id>` on
 *     every notification frame; the lib echoes that as `Last-Event-ID`
 *     on reconnect, which our endpoint uses to replay missed events.
 *     We don't track lastEventId here — the lib does it for us.
 */
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { apiUrl } from "./env";
import { tokens, authApi } from "./api-client";
import type { Notification } from "@/types/api";

type OnNotification = (n: Notification) => void;
type OnConnect = () => void;

interface ConnectOptions {
  onNotification: OnNotification;
  onConnect?: OnConnect;
}

const STREAM_URL = "/api/notifications/stream";
const SSE_CONTENT_TYPE = "text/event-stream";

declare global {
  interface Window {
    /** Original WebView fetch, saved by the Capacitor bridge BEFORE
     *  CapacitorHttp patches window.fetch. Only defined on native. */
    CapacitorWebFetch?: typeof fetch;
  }
}

/**
 * SSE needs a fetch whose response body actually STREAMS. With
 * `CapacitorHttp: { enabled: true }` (capacitor.config.ts) the native app
 * patches window.fetch to run through the native HTTP layer, which buffers
 * the entire response and only resolves when the request COMPLETES — an SSE
 * stream never completes, so the patched fetch never yields a single frame
 * and notifications silently die on mobile while working on web. The bridge
 * keeps the original WebView fetch at window.CapacitorWebFetch; use it when
 * present. (The raw fetch sends `Origin: capacitor://localhost` /
 * `https://localhost`, which the backend CORS allowlist admits explicitly.)
 */
function streamingFetch(): typeof fetch {
  const raw = window.CapacitorWebFetch;
  if (raw) return (input, init) => raw.call(window, input, init);
  return (input, init) => window.fetch(input, init);
}

/** Sentinel error thrown from onopen/onmessage to stop the lib's
 *  built-in retry — used when we've already scheduled our own reopen
 *  (e.g. after refreshing the token), so we don't want a duplicate. */
class StopRetryError extends Error {}

export function connectNotificationsStream(opts: ConnectOptions): () => void {
  let closed = false;
  let ctrl: AbortController | null = null;
  let consecutiveErrors = 0;

  const open = (): void => {
    if (closed) return;
    const access = tokens.access;
    if (!access) {
      // No access token in memory — refresh first, then open. This
      // covers the bootstrap race where the hook mounts before the
      // initial /refresh call has populated the token.
      void authApi.refreshAccessFromCookie().then((ok) => {
        if (ok && !closed) open();
      });
      return;
    }

    ctrl = new AbortController();

    void fetchEventSource(apiUrl(STREAM_URL), {
      // Bearer header reads the LATEST token at the moment of this
      // call. On reconnect we abort + recall open(), so each new
      // attempt picks up a freshly refreshed token if there is one.
      headers: { Authorization: `Bearer ${access}` },
      signal: ctrl.signal,
      // Bypass CapacitorHttp's buffered fetch on native — see streamingFetch.
      fetch: streamingFetch(),
      // Default behaviour closes the stream when the tab is hidden and
      // reopens on visibility. For notifications we want background
      // tabs to keep receiving events.
      openWhenHidden: true,

      async onopen(response) {
        if (
          response.ok &&
          response.headers.get("content-type")?.includes(SSE_CONTENT_TYPE)
        ) {
          consecutiveErrors = 0;
          return;
        }
        if (response.status === 401) {
          // Stale or missing access token. Refresh and reopen with the
          // new one. We throw StopRetryError so the lib doesn't try to
          // reconnect with the same (still-stale) headers — our
          // refresh-then-open below is the authoritative retry path.
          ctrl?.abort();
          const ok = await authApi.refreshAccessFromCookie();
          if (ok && !closed) {
            setTimeout(open, 50);
          }
          throw new StopRetryError("reopening after token refresh");
        }
        // Any other non-OK status: bubble to onerror for backoff.
        throw new Error(`SSE handshake failed: ${response.status}`);
      },

      onmessage(event) {
        if (event.event === "connected") {
          opts.onConnect?.();
          return;
        }
        if (!event.data) return;
        try {
          const data = JSON.parse(event.data) as Notification;
          opts.onNotification(data);
        } catch {
          // Malformed frame — drop it rather than break the stream.
        }
      },

      onclose() {
        // The server closed the connection. Throw to signal the lib's
        // retry logic; honoured by onerror below.
        throw new Error("SSE stream closed by server");
      },

      onerror(err) {
        if (err instanceof StopRetryError || closed) {
          // Propagating the error out of onerror tells the lib to stop
          // retrying. We do this when we've taken over the reopen path
          // (token refresh) or when the consumer has closed us.
          throw err;
        }
        consecutiveErrors += 1;
        // Exponential backoff capped at 30s. The first retry is at 1s.
        return Math.min(30_000, 1000 * 2 ** Math.min(consecutiveErrors - 1, 5));
      },
    });
  };

  open();

  return () => {
    closed = true;
    ctrl?.abort();
  };
}
